import React, { useMemo } from 'react';
import Icon from 'components/AppIcon';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const INCOME_MID_MAP = {
  under_30k: 20000,
  '30k_50k': 40000,
  '50k_75k': 62500,
  '75k_100k': 87500,
  '100k_150k': 125000,
  over_150k: 200000,
};

const CATEGORY_COLORS = {
  housing: '#06B6D4',
  groceries: '#10B981',
  transportation: '#F59E0B',
  utilities: '#8B5CF6',
  dining: '#EF4444',
  entertainment: '#EC4899',
  healthcare: '#06B6D4',
  shopping: '#F97316',
  subscriptions: '#14B8A6',
  savings: '#10B981',
  debt: '#EF4444',
  other: '#94A3B8',
};

const CATEGORY_LABELS = {
  housing: 'Housing',
  groceries: 'Groceries',
  transportation: 'Transport',
  utilities: 'Utilities',
  dining: 'Dining',
  entertainment: 'Entertainment',
  healthcare: 'Healthcare',
  shopping: 'Shopping',
  subscriptions: 'Subscriptions',
  savings: 'Savings',
  debt: 'Debt',
  other: 'Other',
};

export default function BudgetPreview({ formData }) {
  const monthlyIncome = useMemo(() => {
    const base = INCOME_MID_MAP?.[formData?.income?.incomeRange] || 0;
    const additional = parseFloat(formData?.income?.additionalIncome) || 0;
    return Math.round(base / 12 + additional);
  }, [formData?.income]);

  const expenseData = useMemo(() => {
    const expenses = formData?.expenses || {};
    return Object.entries(expenses)?.filter(([, val]) => parseFloat(val) > 0)?.map(([key, val]) => ({
        name: CATEGORY_LABELS?.[key] || key,
        value: parseFloat(val),
        color: CATEGORY_COLORS?.[key] || '#94A3B8',
      }));
  }, [formData?.expenses]);

  const totalExpenses = expenseData?.reduce((s, d) => s + d?.value, 0);
  const remaining = monthlyIncome - totalExpenses;
  const savingsRate = monthlyIncome > 0 ? ((remaining / monthlyIncome) * 100)?.toFixed(1) : 0;

  const selectedGoals = formData?.goals?.selectedGoals || [];
  const goalDetails = formData?.goals?.goalDetails || {};

  const GOAL_LABELS = {
    vacation: 'Vacation',
    emergency: 'Emergency Fund',
    debt: 'Debt Repayment',
    home: 'Home Down Payment',
    retirement: 'Retirement',
    education: 'Education',
  };

  const GOAL_DEFAULTS = {
    vacation: { target: 3000, months: 12 },
    emergency: { target: 10000, months: 18 },
    debt: { target: 5000, months: 24 },
    home: { target: 50000, months: 60 },
    retirement: { target: 100000, months: 120 },
    education: { target: 15000, months: 36 },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon name="BarChart3" size={18} color="#06B6D4" />
        <h3 className="text-sm font-semibold text-[#F8FAFC]">Budget Preview</h3>
        <span className="text-xs text-[#64748B] ml-auto">Live</span>
        <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
      </div>
      {/* Income vs Expenses */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 rounded-lg bg-[rgba(16,185,129,0.08)] border border-[rgba(16,185,129,0.2)]">
          <p className="text-xs text-[#94A3B8] mb-1">Monthly Income</p>
          <p className="font-data text-base font-bold text-[#10B981]">
            ${monthlyIncome?.toLocaleString()}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)]">
          <p className="text-xs text-[#94A3B8] mb-1">Total Expenses</p>
          <p className="font-data text-base font-bold text-[#EF4444]">
            ${totalExpenses?.toLocaleString()}
          </p>
        </div>
      </div>
      {/* Remaining */}
      <div
        className={`p-3 rounded-lg border ${
          remaining >= 0
            ? 'bg-[rgba(6,182,212,0.08)] border-[rgba(6,182,212,0.2)]'
            : 'bg-[rgba(239,68,68,0.08)] border-[rgba(239,68,68,0.2)]'
        }`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-[#94A3B8]">Remaining / Savings</p>
            <p className={`font-data text-lg font-bold ${remaining >= 0 ? 'text-[#06B6D4]' : 'text-[#EF4444]'}`}>
              ${Math.abs(remaining)?.toLocaleString()}
              {remaining < 0 && <span className="text-xs ml-1">deficit</span>}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#94A3B8]">Savings Rate</p>
            <p className={`font-data text-sm font-bold ${parseFloat(savingsRate) >= 20 ? 'text-[#10B981]' : parseFloat(savingsRate) >= 10 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`}>
              {savingsRate}%
            </p>
          </div>
        </div>
      </div>
      {/* Pie Chart */}
      {expenseData?.length > 0 && (
        <div>
          <p className="text-xs text-[#94A3B8] mb-2">Expense Distribution</p>
          <div className="w-full h-36">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={60}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {expenseData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [`$${value?.toLocaleString()}`, '']}
                  contentStyle={{
                    backgroundColor: '#0F172A',
                    border: '1px solid rgba(6,182,212,0.2)',
                    borderRadius: '8px',
                    fontSize: '11px',
                    color: '#CBD5E1',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1 max-h-28 overflow-y-auto">
            {expenseData?.slice(0, 5)?.map((item, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item?.color }} />
                  <span className="text-[#94A3B8] truncate max-w-[80px]">{item?.name}</span>
                </div>
                <span className="font-data text-[#CBD5E1]">${item?.value?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Goals Summary */}
      {selectedGoals?.length > 0 && (
        <div>
          <p className="text-xs text-[#94A3B8] mb-2">Goal Monthly Contributions</p>
          <div className="space-y-1.5">
            {selectedGoals?.map((goalId) => {
              const details = goalDetails?.[goalId] || {};
              const target = parseFloat(details?.target) || GOAL_DEFAULTS?.[goalId]?.target || 0;
              const months = parseFloat(details?.months) || GOAL_DEFAULTS?.[goalId]?.months || 12;
              const monthly = months > 0 ? Math.round(target / months) : 0;
              return (
                <div key={goalId} className="flex items-center justify-between text-xs p-2 rounded bg-[rgba(6,182,212,0.05)] border border-[rgba(6,182,212,0.1)]">
                  <span className="text-[#94A3B8]">{GOAL_LABELS?.[goalId]}</span>
                  <span className="font-data text-[#06B6D4] font-medium">${monthly}/mo</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {monthlyIncome === 0 && expenseData?.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <Icon name="BarChart3" size={32} color="rgba(6,182,212,0.3)" />
          <p className="text-xs text-[#475569]">Fill in your income and expenses to see your budget preview.</p>
        </div>
      )}
    </div>
  );
}